import React from 'react';
import { Link } from 'react-router-dom';
import {
  CircleDot, ShieldCheck, Link2, Droplets, ShieldAlert, FlaskConical,
  Sparkles, Waves, Sun, Snowflake, FlaskRound, Leaf, Timer, ArrowRight,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { getSeo, SEO_KEYWORDS } from '../data/seo';
import { FEATURE_SLUGS, FEATURE_BASE, SITE_URL } from '../data/site';

const icons = [
  CircleDot, ShieldCheck, Link2, Droplets, ShieldAlert, FlaskConical,
  Sparkles, Waves, Sun, Snowflake, FlaskRound, Leaf, Timer,
];

const SpecificFeatures = () => {
  const { t, lang } = useLang();
  const list = t('features.list');
  const seo = getSeo('features', lang);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: seo.title,
    itemListElement: list.map((f, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: f.title,
      url: `${SITE_URL}${FEATURE_BASE}/${FEATURE_SLUGS[i]}`,
    })),
  };

  return (
    <div>
      <SEO title={seo.title} description={seo.description} path="/eigenschaften" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={t('features.label')} title={t('features.title')} lead={t('features.lead')} imageKey="concreteArch2" />
      <section className="bg-white border-b border-slate-200 py-8 lg:py-10">
        <div className="container-wide">
          <div className="max-w-4xl rounded-xl border-l-4 border-blue-700 bg-blue-50/60 px-6 py-5" data-testid="features-disclaimer">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{lang === 'de' ? 'Technische Einordnung' : 'Technical context'}</p>
            <p className="mt-2 text-[14px] text-slate-700 leading-relaxed">
              {lang === 'de'
                ? 'Die beschriebenen Wirkpotenziale von Metakaolin gelten nicht pauschal. Sie h\u00e4ngen unter anderem von Metakaolinqualit\u00e4t, Dosierung, Bindemittel, Wasser-Bindemittel-Wert, Zusatzmitteln, Gesteinsk\u00f6rnung, Nachbehandlung und Expositionsbedingungen ab. F\u00fcr konkrete Anwendungen sind Eignungspr\u00fcfungen erforderlich.'
                : 'The described potential effects of metakaolin do not apply universally. They depend, among other factors, on metakaolin quality, dosage, binder, water-to-binder ratio, admixtures, aggregate, curing and exposure conditions. Suitability testing is required for specific applications.'}
            </p>
          </div>
        </div>
      </section>
      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="container-wide grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {list.map((f, i) => {
            const Icon = icons[i] || CircleDot;
            const slug = FEATURE_SLUGS[i];
            return (
              <Reveal key={i} delay={(i % 3) * 70}>
                <Link
                  to={`${FEATURE_BASE}/${slug}`}
                  className="group h-full flex flex-col gap-3 p-6 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-bold tracking-[0.2em] text-blue-700/50">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-900 leading-snug">{f.title}</h3>
                  <p className="text-[13.5px] text-slate-600 leading-relaxed flex-1">{f.desc}</p>
                  <span className="mt-1 inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all">
                    {t('common.learnMore')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default SpecificFeatures;
